import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { EmptyState } from '../empty-state/empty-state.tsx';
import { CHART_PALETTE, type ChartColor } from '../charts/palette.ts';

export interface FunnelStep {
  /** What happened at this step — "Shown", "Clicked", "Finished". */
  label: string;
  /** How many got this far. */
  value: number;
  /** One quiet line under the label: what the count does and does not include. */
  hint?: ReactNode;
  /** Overrides the palette slot this step would otherwise take. */
  color?: ChartColor;
}

export interface FunnelChartProps {
  steps: FunnelStep[];
  /** Formats every count, e.g. thousands separators. */
  valueFormat?: (value: number) => string;
  /** Names the funnel as a whole for a screen reader. */
  ariaLabel: string;
  /** Shown instead of the bars when every step is zero. */
  emptyTitle?: ReactNode;
  className?: string;
}

const BAR_TONES: Record<ChartColor, string> = {
  moss: 'bg-avatar-moss',
  navy: 'bg-avatar-navy',
  teal: 'bg-avatar-teal',
  plum: 'bg-avatar-plum',
  berry: 'bg-avatar-berry',
  ochre: 'bg-avatar-ochre',
};

function conversion(value: number, previous: number): string {
  /* Nothing arrived, so nothing converted — a 0% would claim it was measured. */
  if (previous === 0) return '–';
  return `${Math.round((value / previous) * 100)}%`;
}

/**
 * How many of the people who reached one step reached the next.
 *
 * The bars are proportional to the first step, so the drop-off is the picture
 * and the percentages only name what the eye already saw. A step that comes
 * out *larger* than the first (a nudge counted once per person but dismissed
 * every time it appeared) widens the scale instead of running off the card.
 *
 * Deliberately not chart.js: this is four rows of text with a coloured length
 * behind them, and a canvas would make the counts unselectable and the labels
 * unwrappable for the sake of drawing rectangles.
 */
export function FunnelChart({
  steps,
  valueFormat,
  ariaLabel,
  emptyTitle = 'No data yet',
  className,
}: FunnelChartProps) {
  const values = steps.map((step) => step.value);
  const scale = Math.max(steps[0]?.value ?? 0, ...values, 1);
  const format = (value: number) => (valueFormat ? valueFormat(value) : String(value));

  if (steps.length === 0 || values.every((value) => value === 0)) {
    return (
      <EmptyState data-slot='funnel-empty' title={emptyTitle} className={className} />
    );
  }

  return (
    <ol
      data-slot='funnel-chart'
      aria-label={ariaLabel}
      className={cn('flex flex-col gap-4', className)}
    >
      {steps.map((step, index) => {
        const previous = index === 0 ? null : steps[index - 1];
        const tone = BAR_TONES[step.color ?? CHART_PALETTE[index % CHART_PALETTE.length]];

        return (
          <li key={step.label} data-slot='funnel-step'>
            {previous !== null && (
              <p data-slot='funnel-conversion' className='mb-1.5 text-xs text-ink-muted'>
                {`↓ ${conversion(step.value, previous.value)} of ${previous.label.toLowerCase()}`}
              </p>
            )}
            <div className='flex items-baseline justify-between gap-3'>
              <span data-slot='funnel-label' className='text-sm font-semibold text-ink'>
                {step.label}
              </span>
              <span
                data-slot='funnel-value'
                className='shrink-0 text-sm font-semibold text-ink tabular-nums'
              >
                {format(step.value)}
              </span>
            </div>
            {step.hint !== undefined && step.hint !== null && (
              <p data-slot='funnel-hint' className='mt-0.5 text-xs text-ink-muted'>
                {step.hint}
              </p>
            )}
            <div
              aria-hidden='true'
              className='mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-fill'
            >
              <div
                data-slot='funnel-bar'
                className={cn('h-full rounded-full transition-[width]', tone)}
                style={
                  { width: `${(step.value / scale) * 100}%` } satisfies CSSProperties
                }
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
}
